<template>
    <v-card>
        <v-card-title>{{ $t('Lukukuittaukset') }}</v-card-title>

        <v-card-text>
            <!-- Loading Indicator -->

            <v-skeleton-loader v-if="loading" type="table-row-divider, table-row@6"></v-skeleton-loader>

            <!-- Simple Table -->
            <v-simple-table v-else-if="!loading && resources.length > 0">
                <template v-slot:default>
                    <thead>
                        <tr>
                            <th v-for="header in headers" :key="header.value" :style="{ width: header.width }">
                                {{ header.text }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in resources" :key="item.id" :class="getRowClass(item)">
                            <!-- Title Column -->
                            <td>
                                <div :class="{ [`ml-${item.level * 4}`]: item.level > 0 }" class="d-flex align-center">
                                    <v-icon v-if="item.type === 'folder'" small class="mr-2 text--secondary">
                                        {{ icons.mdiFolder }}
                                    </v-icon>
                                    <v-icon v-else-if="item.isChild" small class="mr-2 text--secondary">
                                        {{ icons.mdiSubdirectoryArrowRight }}
                                    </v-icon>
                                    <span :class="{ 'font-weight-medium': item.type === 'folder' }">
                                        {{ item.type === 'folder' ? item.folder_title : item.title }}
                                    </span>
                                </div>
                            </td>

                            <!-- Edit Column -->
                            <td>
                                <v-btn v-if="item.type === 'resource'"
                                    :to="{ name: 'admin-resource-form', params: { id: item.id } }" icon x-small
                                    color="primary">
                                    <v-icon small>{{ icons.mdiPencil }}</v-icon>
                                </v-btn>
                            </td>

                            <!-- Version Column -->
                            <td>
                                <v-chip v-if="item.type === 'resource' && item.require_acknowledgment" color="primary"
                                    small>
                                    v{{ item.acknowledgment_version || 1 }}
                                </v-chip>
                            </td>

                            <!-- Acknowledgment Count Column -->
                            <td>
                                <div class="d-flex align-center"
                                    v-if="item.type === 'resource' && item.require_acknowledgment">
                                    <div style="width: 100px;">
                                        <span class="">{{ item.acknowledgment_count || 0 }}</span>
                                        /
                                        <span class="mr-2">{{ item.total_target_users || 0 }}</span>
                                    </div>
                                    <v-progress-linear :value="getAcknowledgmentPercentage(item)"
                                        :color="getProgressColor(getAcknowledgmentPercentage(item))" height="10" rounded
                                        style="min-width: 60px;"></v-progress-linear>
                                </div>
                            </td>

                            <!-- Acknowledgment Rate Column -->
                            <td>
                                <span v-if="item.type === 'resource' && item.require_acknowledgment">
                                    {{ getAcknowledgmentPercentage(item) }}%
                                </span>
                            </td>

                            <!-- Actions Column -->
                            <td>
                                <v-btn v-if="item.type === 'resource' && item.require_acknowledgment"
                                    @click="openStatsDialog(item)" color="primary" small>
                                    <v-icon small left>{{ icons.mdiChartLine }}</v-icon>
                                    {{ $t('Raportti') }}
                                </v-btn>
                            </td>
                        </tr>
                    </tbody>
                </template>
            </v-simple-table>

            <!-- No Data Message -->
            <div v-if="!loading && resources.length === 0" class="text-center pa-4 text--secondary">
                {{ $t('Lukukuittauksia ei ole käytössä') }}
            </div>

            <!-- Stats Dialog -->
            <v-dialog v-model="statsDialog" max-width="1200px" scrollable>
                <v-card style="min-height: 75vh;">
                    <v-card-text class="pa-0" v-if="selectedResourceId">
                        <Acknowledgments :target-type="AcknowledgmentTypes.Resource" :target-id="selectedResourceId"
                            :title="acknowledgmentResourceTitle" :version="acknowledgmentResourceVersion"
                            :key="`ack-res-${selectedResourceId}`">
                        </Acknowledgments>
                    </v-card-text>
                </v-card>
            </v-dialog>
        </v-card-text>
    </v-card>
</template>

<script>
import { onMounted, ref, watch } from "@vue/composition-api";
import adminApi from "@/api/admin";
import {
    mdiChartLine,
    mdiClose,
    mdiPencil,
    mdiFolder,
    mdiSubdirectoryArrowRight
} from "@mdi/js";
import { useTranslations } from "@/@core/libs/i18n";

import Acknowledgments from "@/components/acknowledgements/Acknowledgments.vue"
import AcknowledgmentTypes from "@/@core/utils/acknowledgment_types";

export default {
    components: {
        Acknowledgments
    },
    setup() {
        const resources = ref([]);
        const loading = ref(false);
        const statsDialog = ref(false);
        const selectedResourceId = ref(null);
        const acknowledgmentResourceTitle = ref(null);
        const acknowledgmentResourceVersion = ref(null);

        const { t } = useTranslations();

        const headers = ref([
            { text: t('Tiedosto'), value: 'title', sortable: true },
            { text: '', value: 'edit', sortable: false, width: '50px' },
            { text: t('Versio'), value: 'acknowledgment_version', sortable: true },
            { text: t('Lukukuittaukset'), value: 'acknowledgment_count', sortable: true },
            { text: '', value: 'acknowledgment_rate', sortable: false },
            { text: '', value: 'actions', sortable: false }
        ]);

        const fetchResources = async () => {
            loading.value = true;
            try {
                const response = await adminApi.getResourceAcknowledgmentOverview();
                if (response.ok) {
                    resources.value = buildFolderHierarchy(response.data.resources);
                } else {
                    console.error('Failed to load resources:', response.error);
                }
            } catch (error) {
                console.error('Error fetching resources:', error);
            } finally {
                loading.value = false;
            }
        };

        const buildFolderHierarchy = (resourceList) => {
            const result = [];
            const folderMap = new Map();
            const transformedResources = [];

            // First, transform all resources and organize by folder
            resourceList.forEach(resource => {
                const transformed = {
                    ...resource,
                    type: 'resource',
                    level: 0,
                    isChild: false,
                    // Map acknowledgment_stats to expected properties
                    acknowledgment_count: resource.acknowledgment_stats?.acknowledged_count || 0,
                    pending_count: resource.acknowledgment_stats?.pending_count || 0,
                    total_target_users: resource.acknowledgment_stats?.total_target_users || 0,
                    total_users: resource.acknowledgment_stats?.total_target_users || 0,
                    acknowledgment_rate: resource.acknowledgment_stats?.acknowledgment_rate || 0
                };
                transformedResources.push(transformed);

                // Track folders
                if (resource.folder_id) {
                    const folderId = resource.folder_id;
                    if (!folderMap.has(folderId)) {
                        folderMap.set(folderId, {
                            id: `folder-${folderId}`,
                            folder_id: folderId,
                            folder_title: resource.folder_title,
                            folder_parent_id: resource.folder_parent_id,
                            type: 'folder',
                            level: 0,
                            isChild: false,
                            resources: []
                        });
                    }
                    folderMap.get(folderId).resources.push(transformed);
                }
            });

            // Separate resources without folders
            const rootResources = transformedResources.filter(r => !r.folder_id);

            // Organize folders into parent-child structure
            const parentFolders = [];
            const subfolders = new Map();

            folderMap.forEach(folder => {
                if (folder.folder_parent_id === null) {
                    parentFolders.push(folder);
                } else {
                    if (!subfolders.has(folder.folder_parent_id)) {
                        subfolders.set(folder.folder_parent_id, []);
                    }
                    subfolders.get(folder.folder_parent_id).push(folder);
                }
            });

            // Add folders with their resources first
            parentFolders.forEach(parentFolder => {
                // Add parent folder header
                result.push({
                    ...parentFolder,
                    level: 0,
                    isChild: false
                });

                // Add resources directly in parent folder
                parentFolder.resources.forEach(resource => {
                    if (resource.folder_parent_id === null) {
                        result.push({
                            ...resource,
                            level: 1,
                            isChild: true
                        });
                    }
                });

                // Add subfolders
                const childFolders = subfolders.get(parentFolder.folder_id) || [];
                childFolders.forEach(subfolder => {
                    // Add subfolder header
                    result.push({
                        ...subfolder,
                        level: 1,
                        isChild: true
                    });

                    // Add resources in subfolder
                    subfolder.resources.forEach(resource => {
                        result.push({
                            ...resource,
                            level: 2,
                            isChild: true
                        });
                    });
                });
            });

            // Add root-level resources at the end
            rootResources.forEach(resource => {
                result.push(resource);
            });

            return result;
        };

        const getAcknowledgmentPercentage = (resource) => {
            if (!resource.require_acknowledgment || !resource.total_users) return 0;
            return (((resource.acknowledgment_count || 0) / resource.total_users) * 100).toFixed(2);
        };

        const getProgressColor = (percentage) => {
            if (percentage >= 80) return 'success';
            if (percentage >= 50) return 'warning';
            return 'error';
        };

        const getRowClass = (item) => {
            const classes = [];
            if (item.type === 'folder') classes.push('folder-row');
            if (item.level === 1) classes.push('row-level-1');
            if (item.level === 2) classes.push('row-level-2');
            return classes.join(' ');
        };

        const openStatsDialog = (resource) => {
            selectedResourceId.value = resource.id;
            acknowledgmentResourceTitle.value = resource.title;
            acknowledgmentResourceVersion.value = resource.acknowledgment_version;
            statsDialog.value = true;
        };

        // Watch for dialog close to reset selected resource
        watch(() => statsDialog.value, (newVal) => {
            if (!newVal) {
                selectedResourceId.value = null;
            }
        });

        onMounted(() => {
            fetchResources();
        });

        return {
            resources,
            loading,
            statsDialog,
            selectedResourceId,
            acknowledgmentResourceTitle,
            acknowledgmentResourceVersion,
            headers,
            getAcknowledgmentPercentage,
            AcknowledgmentTypes,
            getProgressColor,
            getRowClass,
            openStatsDialog,
            icons: {
                mdiChartLine,
                mdiClose,
                mdiPencil,
                mdiFolder,
                mdiSubdirectoryArrowRight
            },
            t
        };
    }
};
</script>

<style scoped>
/* Table wrapper for horizontal scroll if needed */
.v-simple-table {
    overflow-x: auto;
}

/* Hierarchical indentation classes */
.ml-4 {
    margin-left: 16px !important;
}

.ml-8 {
    margin-left: 32px !important;
}

/* Table header styling */
.v-simple-table thead th {
    font-weight: 600;
}

/* Ensure proper spacing */
.v-simple-table td {
    padding: 8px 16px;
}

/* Folder row styling */
.v-simple-table tbody tr.folder-row {
    background-color: rgba(0, 0, 0, 0.03);
    font-weight: 500;
}

/* Add subtle background for child resources at different levels */
.v-simple-table tbody tr.row-level-1 {
    background-color: rgba(0, 0, 0, 0.02);
}

.v-simple-table tbody tr.row-level-2 {
    background-color: rgba(0, 0, 0, 0.04);
}

/* Ensure folder rows override level backgrounds */
.v-simple-table tbody tr.folder-row.row-level-1 {
    background-color: rgba(0, 0, 0, 0.05);
}
</style>
